// Shared API helpers for the KSP SOPs tile.
const API_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');

export const SOP_API_BASE = `${API_URL}/sops`;

const authHeaders = (extra = {}) => {
  const token = localStorage.getItem('token');
  return {
    Authorization: token ? `Bearer ${token}` : '',
    ...extra,
  };
};

const handle = async (res) => {
  if (!res.ok) {
    let detail = '';
    let body = null;
    try { body = await res.json(); detail = body.message || body.details || ''; } catch (_) {}
    const err = new Error(detail || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
};

const json = (method, body) => ({
  method,
  headers: authHeaders({ 'Content-Type': 'application/json' }),
  body: JSON.stringify(body),
});

const qs = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length)) return;
    search.set(k, Array.isArray(v) ? v.join(',') : String(v));
  });
  const str = search.toString();
  return str ? `?${str}` : '';
};

/**
 * The file endpoints require an Authorization header, which <img>, <iframe> and
 * <video> cannot send. Everything binary is therefore fetched here and handed
 * to the DOM as an object URL — the same approach the training tile's
 * SlideViewer uses for authenticated slide images.
 *
 * Callers MUST revoke the returned URL on unmount or it leaks the whole blob.
 */
export const fetchBlobUrl = async (url) => {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`File load failed: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

/** Same, for the renderers that want the raw text rather than a blob URL. */
export const fetchText = async (url) => {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`File load failed: ${res.status}`);
  return res.text();
};

export const sopApi = {
  // ---- library ----
  list: (params) => fetch(`${SOP_API_BASE}${qs(params)}`, { headers: authHeaders() }).then(handle),
  recent: () => fetch(`${SOP_API_BASE}/recent`, { headers: authHeaders() }).then(handle),
  get: (id) => fetch(`${SOP_API_BASE}/${id}`, { headers: authHeaders() }).then(handle),
  related: (id) => fetch(`${SOP_API_BASE}/${id}/related`, { headers: authHeaders() }).then(handle),

  recordView: (id) => fetch(`${SOP_API_BASE}/${id}/view`, { method: 'POST', headers: authHeaders() }).then(handle),
  toggleFavorite: (id) => fetch(`${SOP_API_BASE}/${id}/favorite`, { method: 'POST', headers: authHeaders() }).then(handle),
  logSearchClick: (payload) =>
    fetch(`${SOP_API_BASE}/search-click`, json('POST', payload)).then(handle),

  // ---- authoring ----
  // FormData sets its own multipart boundary, so Content-Type must be omitted.
  create: (formData) =>
    fetch(SOP_API_BASE, { method: 'POST', headers: authHeaders(), body: formData }).then(handle),
  update: (id, body) => fetch(`${SOP_API_BASE}/${id}`, json('PUT', body)).then(handle),
  addFileVersion: (id, formData) =>
    fetch(`${SOP_API_BASE}/${id}/versions`, { method: 'POST', headers: authHeaders(), body: formData }).then(handle),
  addNativeVersion: (id, body) =>
    fetch(`${SOP_API_BASE}/${id}/versions`, json('POST', body)).then(handle),
  restoreVersion: (id, versionId) =>
    fetch(`${SOP_API_BASE}/${id}/versions/${versionId}/restore`, { method: 'POST', headers: authHeaders() }).then(handle),
  remove: (id, { hard = false } = {}) =>
    fetch(`${SOP_API_BASE}/${id}${hard ? '?hard=true' : ''}`, { method: 'DELETE', headers: authHeaders() }).then(handle),

  versionStatus: (id, versionId) =>
    fetch(`${SOP_API_BASE}/${id}/versions/${versionId}/status`, { headers: authHeaders() }).then(handle),

  // ---- tags ----
  listTags: () => fetch(`${SOP_API_BASE}/tags`, { headers: authHeaders() }).then(handle),
  createTag: (body) => fetch(`${SOP_API_BASE}/tags`, json('POST', body)).then(handle),
  updateTag: (id, body) => fetch(`${SOP_API_BASE}/tags/${id}`, json('PUT', body)).then(handle),
  mergeTag: (id, targetId) => fetch(`${SOP_API_BASE}/tags/${id}/merge`, json('POST', { targetId })).then(handle),
  deleteTag: (id) => fetch(`${SOP_API_BASE}/tags/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handle),
  addTagAlias: (id, alias) => fetch(`${SOP_API_BASE}/tags/${id}/aliases`, json('POST', { alias })).then(handle),
  deleteTagAlias: (aliasId) =>
    fetch(`${SOP_API_BASE}/tags/aliases/${aliasId}`, { method: 'DELETE', headers: authHeaders() }).then(handle),

  // ---- categories ----
  listCategories: () => fetch(`${SOP_API_BASE}/categories`, { headers: authHeaders() }).then(handle),
  createCategory: (body) => fetch(`${SOP_API_BASE}/categories`, json('POST', body)).then(handle),
  deleteCategory: (id) =>
    fetch(`${SOP_API_BASE}/categories/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handle),

  // ---- insights ----
  insights: (days = 30) =>
    fetch(`${SOP_API_BASE}/insights/searches?days=${days}`, { headers: authHeaders() }).then(handle),

  // ---- raw file URLs (feed these to fetchBlobUrl / fetchText) ----
  fileUrl: (id, versionId) =>
    versionId ? `${SOP_API_BASE}/${id}/versions/${versionId}/file` : `${SOP_API_BASE}/${id}/file`,
  downloadUrl: (id, versionId) =>
    versionId ? `${SOP_API_BASE}/${id}/versions/${versionId}/download` : `${SOP_API_BASE}/${id}/download`,
  thumbUrl: (id) => `${SOP_API_BASE}/${id}/thumb`,
};

/**
 * Download the original file under its real name. Anchor downloads can't carry
 * an auth header either, so the blob is fetched first and released once the
 * click has been dispatched.
 */
export const downloadOriginal = async (id, versionId, filename) => {
  const url = await fetchBlobUrl(sopApi.downloadUrl(id, versionId));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `sop-${id}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

export const formatBytes = (bytes) => {
  if (bytes == null) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = Number(bytes);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1; }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
};

export const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export const formatRelative = (value) => {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(value);
};
